/**
 * Small, allocation-free 2D math helpers shared by the client and the
 * authoritative server. Everything here is pure and dependency free.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export const TAU = Math.PI * 2;

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Frame-rate independent smoothing factor for exponential approach. */
export const damp = (rate: number, dt: number): number => 1 - Math.exp(-rate * dt);

/** Moves `current` toward `target` by at most `maxDelta`. */
export const approach = (current: number, target: number, maxDelta: number): number =>
  current < target ? Math.min(current + maxDelta, target) : Math.max(current - maxDelta, target);

export const distance = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
};

export const wrapAngle = (a: number): number => {
  let r = a % TAU;
  if (r > Math.PI) r -= TAU;
  else if (r < -Math.PI) r += TAU;
  return r;
};

/** Signed shortest angular difference from `from` to `to`. */
export const angleDelta = (from: number, to: number): number => wrapAngle(to - from);

/** Deterministic 32-bit PRNG (mulberry32) so AI behaviour and replays are stable. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Samples a Catmull-Rom spline through the supplied control points.
 * `t` runs 0..1 across the whole curve. Used for smooth AI racing lines.
 */
export function catmullRom(xs: number[], ys: number[], t: number, closed: boolean): Vec2 {
  const n = xs.length;
  const last = closed ? n : n - 1;
  const scaled = clamp(t, 0, 1) * last;
  const i = Math.min(Math.floor(scaled), last - 1);
  const f = scaled - i;
  const idx = (k: number): number => {
    if (closed) return ((k % n) + n) % n;
    return clamp(k, 0, n - 1);
  };
  const p0 = idx(i - 1);
  const p1 = idx(i);
  const p2 = idx(i + 1);
  const p3 = idx(i + 2);
  const f2 = f * f;
  const f3 = f2 * f;
  const x =
    0.5 *
    (2 * xs[p1]! + (-xs[p0]! + xs[p2]!) * f + (2 * xs[p0]! - 5 * xs[p1]! + 4 * xs[p2]! - xs[p3]!) * f2 +
      (-xs[p0]! + 3 * xs[p1]! - 3 * xs[p2]! + xs[p3]!) * f3);
  const y =
    0.5 *
    (2 * ys[p1]! + (-ys[p0]! + ys[p2]!) * f + (2 * ys[p0]! - 5 * ys[p1]! + 4 * ys[p2]! - ys[p3]!) * f2 +
      (-ys[p0]! + 3 * ys[p1]! - 3 * ys[p2]! + ys[p3]!) * f3);
  return { x, y };
}

/** Formats seconds as m:ss.mmm for HUD and results screens. */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--.---';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/** Formats money with thousands separators. */
export function formatMoney(value: number): string {
  return Math.floor(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
